import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { Habit, HabitCompletion } from '../types'; // Import Habit types
import { AuthContextType } from '@/context/AuthContext'; // Adjust import path if needed

// Determine Base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api' // Use relative path for production (Nginx proxies /api/)
  : 'http://localhost:8000/api'; // Use full path for local dev

// Create an Axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Request Interceptor ---
// Adds the JWT access token to the Authorization header if available
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // In a real app, get the token from localStorage, sessionStorage, or context
    const token = localStorage.getItem('access_token'); // Placeholder storage
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// --- Response Interceptor ---
// Handles token refreshing logic (simplified example)
// A more robust implementation would handle concurrent requests, token refresh failures, etc.
let isRefreshing = false;
let failedQueue: { resolve: (value: unknown) => void; reject: (reason?: any) => void; }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Any status code within 2xx range triggers this
    return response;
  },
  async (error: AxiosError) => {
    // Any status codes outside 2xx range triggers this
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Check if the error is 401 (Unauthorized) and it's not a token refresh request itself
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, add request to queue
        return new Promise(function(resolve, reject) {
          failedQueue.push({resolve, reject});
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return axios(originalRequest); // Use base axios for retrying
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true; // Mark request as retried
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token'); // Placeholder storage

      if (refreshToken) {
        try {
          console.log('Attempting token refresh...');
          const response = await axios.post(`${API_BASE_URL}/token/refresh/`, {
            refresh: refreshToken,
          });
          const newAccessToken = response.data.access;
          
          // Store new token (adapt storage method as needed)
          localStorage.setItem('access_token', newAccessToken);
          console.log('Token refresh successful');

          // Update the Authorization header for the original request and the apiClient defaults
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          
          processQueue(null, newAccessToken); // Process queue with new token
          return apiClient(originalRequest); // Retry the original request with the new token
        } catch (refreshError: any) {
          console.error('Token refresh failed:', refreshError);
          // Handle refresh token failure (e.g., redirect to login)
          processQueue(refreshError, null); // Process queue with error
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          // Consider redirecting to login: window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        console.log('No refresh token available. Redirecting to login.');
        // Handle no refresh token case (e.g., redirect to login)
        localStorage.removeItem('access_token');
        // Consider redirecting: window.location.href = '/login';
      }
    }

    // For errors other than 401 or if refresh fails, reject the promise
    return Promise.reject(error);
  }
);

// --- Habit API Functions ---

// Fetch all active habits for the user
export const getHabits = async (): Promise<Habit[]> => {
  const response = await apiClient.get<Habit[]>('/habits/habits/'); // Ensure trailing slash if needed by DRF router
  return response.data;
};

// Fetch habit completions (can filter by date, habit_id etc. via query params)
export const getHabitCompletions = async (params?: { date?: string; start_date?: string; end_date?: string; habit_id?: number }): Promise<HabitCompletion[]> => {
  const response = await apiClient.get<HabitCompletion[]>('/habits/completions/', { params });
  return response.data;
};

// Toggle habit completion for a specific date (simplified)
export const toggleHabitCompletion = async (habitId: number, date: string, currentState: boolean): Promise<void> => {
  const url = `/habits/habits/${habitId}/toggle-completion/`;
  const payload = { completion_date: date };

  if (currentState) {
    // If currently completed (checked), send DELETE to unmark
    // The backend handles the case where it might already be deleted.
    await apiClient.delete(url, { data: payload });
  } else {
    // If currently incomplete (unchecked), send POST to mark
    // The backend handles the case where it might already be created.
    await apiClient.post(url, payload);
  }
};

// Create a new habit
export const createHabit = async (habitData: Omit<Habit, 'id' | 'user' | 'created_at' | 'updated_at'>): Promise<Habit> => {
  const response = await apiClient.post<Habit>('/habits/habits/', habitData);
  return response.data;
};

// Update an existing habit
export const updateHabit = async (habitId: number, habitData: Partial<Omit<Habit, 'id' | 'user' | 'created_at' | 'updated_at'>>): Promise<Habit> => {
  const response = await apiClient.patch<Habit>(`/habits/habits/${habitId}/`, habitData);
  return response.data;
};

// Delete a habit
export const deleteHabit = async (habitId: number): Promise<void> => {
  await apiClient.delete(`/habits/habits/${habitId}/`);
};

export default apiClient; 